
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import {
  Project_Activity,
  Project_Activity_Time,
  Project_Activity_Request_More_Hours,
  Skill,
  User,
  User_Profile_By_Management,
} from "@/generated/graphql";
import { Project } from "@/interface";
import Badge from "@/components/common/BadgeNew.vue";
import BadgeColor from "@/entities/BadgeColor";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModalNew.vue";
import { NOTIFICATION } from "@/entities/Events";
import UPDATE_PROJECT_ACTIVITY from "@/apollo/projectActivities/UpdateActivity.gql";
import DELETE_PROJECT_ACTIVITY from "@/apollo/projectActivities/DeleteActivity.gql";
import Message from "@/data/notificationsMessages";
import StatusBadge from "@/views/manage/projectDetails/StatusBadge.vue";
import NotesModal from "@/components/projects/activities/NotesModal.vue";
import RouteParams from "@/entities/RouteParams";
import MoreActions from "@/components/common/MoreActionsClick.vue";
import RouteNames from "@/entities/RouteNames";
import Roles from "@/entities/Roles";
import ItemChip from "@/components/common/ItemChipNew.vue";
import UiButton from "@/components/button/ui-button.vue";
import { defaultErrorMessage } from "@/data/errorMessages";
import ItemChipsWithMore from "@/components/common/ItemChipsWithMoreCustom.vue";
import ItemChipsData from "@/entities/ItemChipsData";
import SuitableResources from "@/components/projects/activities/SuitableResourcesV2.vue";
import EmployeeRemovedFromCompanyTooltip from "@/components/tooltip/EmployeeRemovedFromCompanyTooltip.vue";
import ContractorReviewCard from "@/components/companyContractors/ContractorReviewCard.vue";
import moment from "moment";
import PROJECT_ACTIVITY_TIME_AGGREGATE from "@/apollo/projectActivities/ProjectActivityTimeAggregate.gql";
import RequestedHours from "@/components/projects/timeTracker/RequestedHours.vue";
import RequestMoreHoursDeclineReasonModal from "@/components/projects/timeTracker/RequestMoreHoursDeclineReasonModal.vue";
import ManagementTimeTrackerModal from "@/components/projects/timeTracker/ManagementTimeTrackerModalNew.vue";
import DeclineReasonModal from "@/components/projects/timeTracker/DeclineReasonModal.vue";
import S3Image from "@/components/common/s3-image.vue";
import visitTypeDisplayMap from "@/entities/VisitType";
import DeclinedReasonConversationModal from "@/components/projects/timeTracker/DeclinedReasonConversationModal.vue";
import PROJECT_ACTIVITY_CREATED_REQUEST_MORE_HOURS_AGGREGATE from "@/apollo/projectActivities/ProjectActivityCreatedRequestMoreHours.gql";
import { hslColorFromName } from "@/utils/hslColorFromName";
import getAuthData from "@/utils/getAuthData";
import GET_PROFILE_DETAILS_BY_MANAGEMENT_ALL from "@/apollo/profile/profileDetails/management/GetProfileDetailByManagementAll.gql";
import {
  displayTimeInLocationTimezone,
  deserializeTimezone,
} from "@/utils/timezoneUtils";

interface ContractorChip {
  name: string;
  id: string;
  route: string;
  routeParams: RouteParams<{ id: string }>;
  avatar?: string | null;
  deleted?: boolean;
}

interface ConfirmFinalReportArgs {
  name: string;
  value: string;
  action: () => void;
}
declare module "vue/types/vue" {
  interface Vue {
    $moment: typeof moment;
  }
}

@Component({
  components: {
    ContractorReviewCard,
    NotesModal,
    S3Image,
    SuitableResources,
    MoreActions,
    ItemChipsWithMore,
    EmployeeRemovedFromCompanyTooltip,
    UiButton,
    ItemChip,
    ActivityFormModal,
    RequestedHours,
    RequestMoreHoursDeclineReasonModal,
    ManagementTimeTrackerModal,
    DeclineReasonModal,
    Badge,
    StatusBadge,
    DeclinedReasonConversationModal,
  },
  apollo: {
    timeAggregate: {
      query: PROJECT_ACTIVITY_TIME_AGGREGATE,
      variables() {
        return {
          activityId: this.activity?.id,
        };
      },
      update: (data) => data.project_activity_time_aggregate.aggregate.sum.time,
    },
    profilesByManagement: {
      query: GET_PROFILE_DETAILS_BY_MANAGEMENT_ALL,
      variables() {
        return {
          userId: this.activity.contractor?.user.id,
        };
      },
      update(data) {
        this.profilesByManagement = data.user_profile_by_management;
        return data.user_profile_by_management;
      },
      skip() {
        return !this.activity || !this.activity.contractor?.user?.id;
      },
    },
    $subscribe: {
      timeAggregate: {
        query: PROJECT_ACTIVITY_TIME_AGGREGATE,
        variables() {
          return {
            activityId: this.activity?.id,
          };
        },
        update: (data) =>
          data.project_activity_time_aggregate.aggregate.sum.time,
      },
    },
    pendingMoreHoursRequest: {
      query: PROJECT_ACTIVITY_CREATED_REQUEST_MORE_HOURS_AGGREGATE,
      variables() {
        return {
          activityId: this.activity?.id,
        };
      },
      update: (data) =>
        data.project_activity_request_more_hours_aggregate.aggregate.sum.hours,
    },
  },
})
export default class ManagementActivityCard extends Vue {
  @Prop({ type: Object, required: true }) readonly activity!: Project_Activity;
  @Prop({ type: Object, required: true }) readonly project!: Project;

  @Prop({ type: Boolean, default: false })
  readonly redirectToContractor!: object;
  @Prop({ type: Boolean, default: false }) readonly frozen!: boolean;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: String, default: undefined }) readonly companyId?: string;
  @Prop({ type: String, required: true }) readonly currentUser: User;
  @Prop({ type: String, required: true }) readonly currentUserRole!: string;

  activityModalOpened: boolean = false;
  processingCompleteActivity: boolean = false;
  processing: boolean = false;
  processingConfirmation: boolean = false;
  job_title: string = "";
  profilesByManagement: User_Profile_By_Management[] = [];
  actionsPopUpShown: boolean = false;
  reviewsShown: boolean = false;
  requestedHoursModalOpened = false;
  timeAggregate: number = 0;
  pendingMoreHoursRequest: number = 0;
  managementTimeTrackerModal: boolean = false;
  showDeclineReasonModal: boolean = false;
  selectedTime: Project_Activity_Time = undefined;
  BadgeColor: typeof BadgeColor = BadgeColor;
  selectedHours: Project_Activity_Request_More_Hours = undefined;
  showNotesModal: boolean = false;

  requestMoreHoursDeclineReasonModal: boolean = false;

  get initial(): string {
    const firstName = this.activity.contractor?.user?.given_name;
    const lastName = this.activity.contractor?.user?.family_name;

    if (!firstName && !lastName) {
      return "";
    }

    const givenName = firstName ? firstName.trimLeft()[0] : "";
    const familyName = lastName ? lastName.trimLeft()[0] : "";
    return `${givenName}${familyName}`;
  }

  get bgStyle(): object {
    return {
      background: hslColorFromName(this.initial || ""),
    };
  }

  get emptyActive(): boolean {
    if (!this.activity) {
      return false;
    }

    return !this.activity?.title.length ? true : false;
  }

  private updateBasicActivityData(
    id: string,
    activity: Partial<Project_Activity>
  ) {
    return this.$apollo.mutate({
      mutation: UPDATE_PROJECT_ACTIVITY,
      variables: {
        id,
        set: {
          drafted: false,
        },
      },
    });
  }

  get googleMapsLink(): string {
    const baseMapsUrl = "https://www.google.com/maps/search/?api=1";
    const queryParam = `query=${encodeURIComponent(this.location)}`;
    return `${baseMapsUrl}&${queryParam}`;
  }

  async onPublish(id, activityData) {
    try {
      await this.updateBasicActivityData(id, activityData);
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(`Activity published successfully.`)
      );
      this.$emit("activityUpdated");
    } catch {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(defaultErrorMessage)
      );
    }
  }

  handleShow() {
    this.showNotesModal = true;
  }

  handleClose() {
    this.showNotesModal = false;
  }

  onEdit(): void {
    this.activityModalOpened = true;
  }

  get skills(): ItemChipsData[] {
    return this.activity.project_activity_skills.map(
      ({ skill }) => skill as ItemChipsData
    );
  }

  async onRemove(): Promise<void> {
    try {
      this.$confirm({
        title: "Remove task",
        message: `Are you sure? (Task will be removed!)`,
        button: {
          no: "No",
          yes: "Yes",
        },
        callback: async (confirm: any) => {
          if (confirm) {
            this.processing = true;
            await this.removeActivityById(this.activity.id);

            this.processing = false;
            this.$emit("activityDeleted", this.activity.id);

            this.$eventBus.emit(
              NOTIFICATION,
              Message.successMessage("Task was deleted successfully.")
            );
          }
        },
      });
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }

  visitUpdated(): void {
    this.$router.go(0);
  }

  get jobTitle(): string {
    if (
      this.activity &&
      this.activity.contractor &&
      this.activity.contractor?.user?.profile?.job_title
    ) {
      return this.activity?.contractor?.user?.profile?.job_title || "n/a";
    } else if (this.profilesByManagement.length > 0) {
      return this.profilesByManagement[0]?.job_title || "n/a";
    } else {
      return "n/a";
    }
  }
  getVisitType(type: string | undefined): string {
    if (!type) return "n/a";
    return (
      visitTypeDisplayMap[type] ||
      type
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
    );
  }

  loadLocationTimezone(tz) {
    if (!tz) return null;
    try {
      const timezone = deserializeTimezone(tz);
      if (!timezone) {
        return null;
      }
      return timezone;
    } catch (error) {
      console.error("Error loading timezone:", error);
      return null;
    }
  }

  formattedTime(time, timezone): string {
    if (!time) return "n/a";
    const locationTimezone = this.loadLocationTimezone(timezone);
    if (locationTimezone) {
      return displayTimeInLocationTimezone(time, locationTimezone, "h:mm A");
    } else {
      return moment(time).format("h:mm A");
    }
  }

  get typeOfVisit(): string {
    return this.getVisitType(this.activity?.type_of_visit);
  }

  get location(): string {
    return this.activity?.location || "n/a";
  }
  get startDate(): string {
    if (!this.activity.start_date) {
      return undefined;
    }

    return moment(this.activity.start_date).format("DD MMM YYYY");
  }

  get checkedInTime(): string {
    if (!this.activity?.checkin_time) {
      return "n/a";
    }
    try {
      const checkInTime = this.$moment(this.activity.checkin_time);
      return checkInTime.isValid()
        ? checkInTime.format("h:mm A")
        : "Invalid Time";
    } catch (error) {
      return "Invalid Time";
    }
  }

  get checkedOutTime(): string {
    if (!this.activity?.checkout_time) {
      return "n/a";
    }
    try {
      const checkOutTime = this.$moment(this.activity.checkout_time);
      return checkOutTime.isValid()
        ? checkOutTime.format("h:mm A")
        : "Invalid Time";
    } catch (error) {
      return "Invalid Time";
    }
  }

  get startTime(): string {
    if (!this.activity?.start_time) return "n/a";
    return this.formattedTime(this.activity.start_time, this.activity.timezone);
  }
  get endTime(): string {
    if (!this.activity?.end_time) return "n/a";
    return this.formattedTime(this.activity.end_time, this.activity.timezone);
  }

  get checkInColor() {
    if (this.activity.checkin_status === "OUTSIDE") {
      const startTime = this.$moment(this.activity.start_time);
      const inWithInWindow = this.$moment(this.activity.checkin_time).isBetween(
        startTime.subtract(10, "minutes"),
        startTime.subtract(5, "minutes")
      );
      if (inWithInWindow) {
        return "text-badge"; // green
      }
    }
    if (
      this.activity.checkin_status === "LATE" ||
      this.activity.checkin_status === "EARLY" ||
      this.activity.checkin_status === "OUTSIDE"
    ) {
      return "text-alert-red"; // red
    }
    if (this.activity.checkin_status === "ON_TIME") {
      return "text-badge"; // green
    }
    if (!this.activity?.checkin_time || !this.activity?.start_time) {
      return "text-gray-500"; // Default gray if times are unavailable
    }
    return "text-primary-black";
  }

  get checkOutColor() {
    if (this.activity.checkin_status === "OUTSIDE") {
      const endTime = this.$moment(this.activity.end_time);
      const inWithInWindow = this.$moment(
        this.activity.checkout_time
      ).isBetween(endTime, endTime.add(5, "minutes"));
      if (inWithInWindow) {
        return "text-badge"; // green
      }
    }

    if (
      this.activity.checkout_status === "LATE" ||
      this.activity.checkout_status === "EARLY" ||
      this.activity.checkout_status === "OUTSIDE"
    ) {
      return "text-alert-red"; // red
    }
    if (this.activity.checkout_status === "ON_TIME") {
      return "text-badge"; // green
    }
    if (!this.activity?.checkout_time || !this.activity?.end_time) {
      return "text-gray-500"; // Default gray if times are unavailable
    }

    return "text-primary-black"; // Default color (If it's early or within the window)
  }

  get actionsHidden(): boolean {
    return this.frozen || this.activity.final_report_confirmed;
  }

  get avatarStyleClasses(): Record<string, boolean> {
    return {
      ["border border-primary"]: true,
    };
  }
  get defaultImageClasses(): string {
    return "text-storm-gray text-sm font-medium rounded-full border border-gray-300 bg-white w-6 h-6 mr-1";
  }
  get defaultInitialsClasses(): string {
    return "inline-flex h-6 w-6 rounded-full items-center justify-center";
  }

  get skillIds(): number[] {
    return this.requestSkills.map(({ id }: Skill) => id);
  }

  get requestSkills(): Skill[] {
    return this.activity.project_activity_skills.map(({ skill }) => skill);
  }

  get excludeResourceUserId(): string {
    return this.activity?.contractor?.user.id;
  }

  get contractorId(): string | undefined {
    return this.activity.contractor?.id;
  }

  get contractor(): ContractorChip {
    if (this.activity && this.activity.contractor) {
      const contractor = this.activity!.contractor!;
      const user = contractor?.user;
      // const { deleted } = contractor;

      let routeParams: RouteParams<{ id: string }> = {
        name: RouteNames.CONTRACTOR_PUBLIC_PAGE,
        params: { id: user.id },
      };

      const contractorId = contractor?.id;
      const managerIds = contractor?.managers.map((m) => m.manager.user_id);
      const contractorDetailsRouteName = RouteNames.CONTRACTOR_DETAILS_ABOUT;

      if (
        managerIds.includes(
          this.currentUserId || this.currentUserRole === Roles.ADMIN
        )
      ) {
        routeParams = {
          name: contractorDetailsRouteName,
          params: { id: contractorId },
        };
      }

      return {
        name: `${user.given_name} ${user.family_name}`,
        avatar: user.avatar,
        id: contractorId,
        route: contractorDetailsRouteName,
        routeParams,
        // deleted,
      };
    }
    return;
  }

  get contractorExists(): boolean {
    return Boolean(
      this.activity.contractor_id || this.activity.contractor?.user
    );
  }

  closeModal(): void {
    this.activityModalOpened = false;
  }

  onActivityUpdated(): void {
    this.$emit("activityUpdated");
    this.closeModal();
  }
  async reopenActivity(): Promise<void> {
    await this.updateActivityStatus("completed", false);
  }
  async completeActivity(): Promise<void> {
    await this.updateActivityStatus("completed", true);
  }

  async cancelActivity(): Promise<void> {
    await this.updateActivityStatus("cancelled", true);
  }

  async triggerCanceledVisitEmail(activity) {
    const uri = `${process.env.VUE_APP_LAMBDA}/send-visit-cancel-email`;
    const { headers } = await getAuthData();
    const { data }: any = await this.axios.post(
      uri,
      {
        data: {
          activity,
        },
      },
      { headers }
    );
  }

  async updateActivityStatus(
    statusKey: "completed" | "cancelled",
    statusValue: boolean
  ): Promise<void> {
    this.processingCompleteActivity = true;

    try {
      const id = this.activity.id;

      // Prepare the variables for the mutation
      const variables: Record<string, boolean> = { [statusKey]: statusValue };

      // If cancelling, also set completed to true
      if (statusKey === "cancelled" && statusValue === true) {
        variables.completed = true;
      }

      // Perform the mutation
      const resp = await this.updateActivityById({ id, set: variables });

      // Determine the event name and success message
      let eventName = "";
      let successMessage = "";

      if (statusKey === "completed") {
        eventName = statusValue ? "activityCompleted" : "activityReopened";
        successMessage = statusValue
          ? "Visit was marked as completed."
          : "Visit was reopened.";
      } else if (statusKey === "cancelled") {
        this.triggerCanceledVisitEmail(
          resp?.data?.update_project_activity_by_pk
        );
        eventName = "activityCancelled";
        successMessage = "Visit was cancelled and marked as completed.";
      }

      // Emit events and show notifications
      this.$emit(eventName, id);
      this.$eventBus.emit(NOTIFICATION, Message.successMessage(successMessage));
    } catch (e) {
      const errorMessage =
        statusKey === "completed"
          ? statusValue
            ? "Something went wrong. Visit was not marked as completed."
            : "Something went wrong. Visit was not reopened."
          : "Something went wrong. Visit was not cancelled.";
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(errorMessage));
    } finally {
      this.processingCompleteActivity = false;
    }
  }

  async updateActivityById({
    id,
    set,
  }: {
    id: string;
    set: Record<string, boolean>;
  }) {
    try {
      return await this.$apollo.mutate({
        mutation: UPDATE_PROJECT_ACTIVITY,
        variables: {
          id,
          set,
        },
      });
    } catch (error) {
      console.error("Error updating activity:", error);
      throw error;
    }
  }

  async removeActivityById(id: string): Promise<void> {
    await this.$apollo.mutate({
      mutation: DELETE_PROJECT_ACTIVITY,
      variables: {
        id,
        contractorId: this.contractorId,
        projectId: this.project.id,
      },
    });
  }
  openRequestedHoursModal(): void {
    this.requestedHoursModalOpened = true;
  }

  updateTimeTracked(): void {
    this.$apollo.queries.timeAggregate.refetch();
  }
  fetchNewActivity(): void {
    this.$router.go(0);
  }
}
